import LegalLayout from "./LegalLayout";

export default function Privacy() {
  const effective = "February 2026";
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle={`Effective ${effective}. This is a template — please review with a lawyer before public launch.`}
    >
      <p>
        Accountrix (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) provides a personal-finance tracking application (the &quot;Service&quot;). This Privacy Policy explains what we collect, why, how we use it, and the choices you have. By using Accountrix, you agree to the practices described here.
      </p>

      <h2>1. Information we collect</h2>
      <h3>Account information</h3>
      <ul>
        <li><b>Name and email address</b> you provide at signup.</li>
        <li><b>Password</b>, stored as a bcrypt hash — never in plaintext.</li>
        <li>Preferences such as currency, theme, notification settings.</li>
      </ul>
      <h3>Financial information you enter</h3>
      <ul>
        <li>Transactions, budgets, savings goals, recurring rules, accounts and investment holdings you create in the app.</li>
        <li>Transactions imported via connected bank providers (see Section 3).</li>
      </ul>
      <h3>Automatically collected</h3>
      <ul>
        <li>IP address (used for brute-force protection on login).</li>
        <li>Session cookies (httpOnly, Secure, SameSite=Lax) that authenticate you.</li>
        <li>Web-push subscription endpoints (only if you enable push notifications).</li>
      </ul>
      <p>
        <b>We do not collect</b> device advertising IDs, precise geolocation, contacts, or biometric data. We do not use third-party advertising or analytics trackers.
      </p>

      <h2>2. How we use your information</h2>
      <ul>
        <li>To provide the core features: budgets, analytics, insights, reports, notifications.</li>
        <li>To send transactional emails (password reset, budget alerts, weekly digest) via our email provider.</li>
        <li>To generate AI insights (see Section 4).</li>
        <li>To protect the Service — rate-limiting, fraud prevention, security incident response.</li>
      </ul>

      <h2>3. Bank integrations (Plaid & Mono)</h2>
      <p>
        If you connect a bank account, we use <b>Plaid</b> (US/EU) or <b>Mono</b> (Nigeria) as an intermediary. When you go through their consent flow, you authenticate directly with your bank — Accountrix never sees your online-banking credentials. Plaid and Mono then send us a scoped access token and your transaction data (amount, merchant, date, category). Their own privacy policies apply:
      </p>
      <ul>
        <li><a href="https://plaid.com/legal/#end-user-privacy-policy" target="_blank" rel="noreferrer">Plaid End User Privacy Policy</a></li>
        <li><a href="https://mono.co/legal/privacy-policy" target="_blank" rel="noreferrer">Mono Privacy Policy</a></li>
      </ul>
      <p>
        You can unlink any bank at any time from <i>Banks</i> in the app; we call the provider&apos;s revoke endpoint and delete our stored access token.
      </p>

      <h2>4. AI features</h2>
      <p>
        When you tap &quot;Generate insights&quot; or receive an AI weekly digest, we send a <b>summarised</b> version of your data (totals per category, top spend, monthly totals) to our LLM provider (Anthropic Claude via the Emergent LLM Key). We do not send individual transaction notes, personal names, or bank account numbers. Prompts and responses are not used to train the model.
      </p>

      <h2>5. Third-party service providers</h2>
      <ul>
        <li><b>Plaid, Mono</b> — bank data aggregation.</li>
        <li><b>Anthropic</b> — AI insight generation.</li>
        <li><b>Resend</b> — transactional email delivery.</li>
        <li><b>CoinGecko, Yahoo Finance</b> — public market prices for the investments page (no user data is sent).</li>
        <li><b>Emergent Cloud</b> — application hosting.</li>
      </ul>

      <h2>6. Retention & deletion</h2>
      <p>
        Your data is retained while your account is active. If you request deletion, we erase your account and all associated financial records within 30 days. Automated backups may persist for up to 90 additional days before being overwritten. Demo accounts auto-delete after 24 hours.
      </p>

      <h2>7. Security</h2>
      <p>
        Passwords are stored using bcrypt; sessions use httpOnly cookies over HTTPS with server-side token revocation on logout and password change. Rate limits and login lockouts protect against automated attacks. No system is 100% secure — please use a strong, unique password and enable web-push alerts to spot unusual activity quickly.
      </p>

      <h2>8. Your rights</h2>
      <p>
        Depending on where you live, you may have rights to access, correct, export, or delete your data (GDPR, CCPA, NDPR). To exercise any of these, email us from the address on your account and we will respond within 30 days.
      </p>

      <h2>9. Children</h2>
      <p>
        Accountrix is not intended for people under 16. We do not knowingly collect data from children.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        We may update this policy occasionally. Material changes will be announced by email or in-app notification at least 14 days before they take effect. Continued use of Accountrix after changes constitutes acceptance.
      </p>

      <h2>11. Contact</h2>
      <p>
        Questions or requests? Email us from the address associated with your Ledger account and we will respond within 30 days.
      </p>
    </LegalLayout>
  );
}
